import { useCombinedQueriesTeamGeneralInfo, useQueryUser } from '@heidi/workspace/data';

import { useReverseTrial } from '../../../data/injected-billing';
import { useQueryComplimentaryUsage } from '../../../data/query/useQueryComplimentaryUsage';
import { useQueryCreditAllowance } from '../../../data/query/useQueryCreditAllowance';
import { useQueryUserBillingInfo } from '../../../data/query/useQueryUserBillingInfo';
import { getCommerceBillingUiPorts } from '../../settings/billing/billing-ui-ports';
import { isHeidi2OptInCreditUsageEnabled } from '../../settings/is-credit-usage-surface-enabled';
import { isMaxBillingSeat } from '../../settings/is-max-billing-seat';
import { showsCreditUsageMeterTrialCountdown } from '../../settings/shows-credit-usage-meter-trial-countdown';
import {
  type ComplimentaryTrialEndedUsageClass,
  getComplimentaryTrialEndedUsageClass,
} from './complimentary-trial-ended-eligibility';

export type ComplimentaryTrialEndedFlags = {
  isHeidi2OptInEnabled: boolean;
  isTrialEndEnabled: boolean;
};

export function useComplimentaryTrialEndedUsageClass({
  isHeidi2OptInEnabled,
  isTrialEndEnabled,
}: ComplimentaryTrialEndedFlags): ComplimentaryTrialEndedUsageClass | null {
  const { data: user } = useQueryUser();
  const { hasTeam, joinedTeamsData } = useCombinedQueriesTeamGeneralInfo();
  const { data: billingInfo } = useQueryUserBillingInfo();
  const { isClinicianTrialUser, isClinicianPracticeTrialUser } = useReverseTrial();

  const isCohortEnabled = isHeidi2OptInCreditUsageEnabled({
    isHeidi2OptInEnabled,
    heidiVersion: billingInfo?.heidi_version,
  });
  const isExcluded =
    !!user?.is_managed ||
    !!hasTeam ||
    isMaxBillingSeat(billingInfo) ||
    showsCreditUsageMeterTrialCountdown({
      isClinicianTrialUser,
      isClinicianPracticeTrialUser,
    });
  const isSettled = user != null && joinedTeamsData != null && billingInfo != null;
  const isAppleCompliantBuild = getCommerceBillingUiPorts().isAppleCompliantBuild();
  const canQuery =
    isSettled &&
    isCohortEnabled &&
    isTrialEndEnabled &&
    !isAppleCompliantBuild &&
    !isExcluded;

  const { data: usage } = useQueryComplimentaryUsage({
    hasUser: !!user,
    enabled: canQuery,
  });
  const { data: allowance } = useQueryCreditAllowance({
    hasUser: !!user,
    enabled: canQuery,
  });

  return getComplimentaryTrialEndedUsageClass({
    isSettled,
    isCohortEnabled,
    isTrialEndEnabled,
    isAppleCompliantBuild,
    isExcluded,
    consumed: usage?.consumed,
    displayLimit: allowance?.display_limit,
  });
}
